import { notification } from 'antd';
import { FormInstance, RuleObject, RuleRender } from 'antd/es/form';
import { format } from 'date-fns';
import { useMediaQuery } from 'react-responsive';
import { AnyObject } from 'yup/lib/types';
import { TErrorProps, TValidationError } from '../Modules/Inertia/Entities';

/* 
    How to Use
    
    1. Define: 
        const yupSync = createYupSync(schema)
    2. Assign to props rules in FormItem
        rules={[yupSync]}
*/

export const createYupSync = (schema: AnyObject): RuleObject | RuleRender => {
    const ruleWithCtx =
        (fieldName?: string): RuleRender =>
        (ctx: FormInstance): RuleObject => {
            const { getFieldsValue } = ctx;
            return {
                // field using any type to avoid issue type from ant design
                async validator({ field }: any) {
                    await schema.validateSyncAt(field, getFieldsValue());
                },
                required: schema.fields[fieldName]?.exclusiveTests?.required,
            };
        };

    function rule(param: string): RuleRender;
    function rule(param: FormInstance): RuleObject;
    function rule(param) {
        if (typeof param === 'string') return ruleWithCtx(param) as RuleRender;
        else return ruleWithCtx(undefined)(param) as RuleObject;
    }

    return rule;
};

export const isMobileScreen = () => {
    return useMediaQuery({ query: '(max-width: 767px)' });
};

export const removeDuplicateArrObj = (
    data: { key: string; value: string }[],
) => {
    return data.filter(
        (el, index) => data.map((el) => el.key).indexOf(el.key) === index,
    );
};

export const removeDuplicateArrStrings = (data: string[]) => {
    return data.filter((el, index) => data.indexOf(el) === index);
};
export const setServerError = (
    error: TErrorProps,
    setFields: (fields: any[]) => void,
): void => {
    const fieldErrors = error?.errors as TValidationError[];
    if (error?.statusCode === 422 && fieldErrors?.length > 0) {
        // set server error validation
        fieldErrors?.map((err) => {
            setFields([
                {
                    name: err.property,
                    errors: err.message,
                },
            ]);
        });
    } else if (error?.message) {
        notification.error({
            message: error.message,
        });
    }
};

export const formatCurrency = (number: number): string => {
    const currency = number?.toLocaleString('id-Id');

    return 'Rp' + currency;
};

export const formatDatetime = (
    date: string | Date,
    pattern = 'yyyy-MM-dd HH:ii:ss',
): string => {
    return format(new Date(date), pattern);
};

export const formatDate = (
    date: string | Date,
    pattern = 'yyyy-MM-dd',
): string => {
    return format(new Date(date), pattern);
};

export const debounce = (fn: (...args: any[]) => any, delay: number) => {
    let timer;
    return (...args: any[]) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
};

export const countDays = (joinDate: Date, exitDate: Date) => {
    const startDate = new Date(joinDate);
    const endDate = new Date(exitDate);

    const timeDiff = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return diffDays;
};

export const toSnakeCase = (str = '') => {
    const strArr = str.split(' ');
    const snakeArr = strArr.reduce((acc, val) => {
        return acc.concat(val.toLowerCase());
    }, []);
    return snakeArr.join('_');
};

export const getParamsURI = () => {
    const urlString: string = window.location.search;
    const urlParams: string[] = urlString.substring(1).split('&');
    const paramsObj = {};
    urlParams.forEach(function (param) {
        const keyValue: string[] = param.split('=');
        const key: string = decodeURIComponent(keyValue[0]);
        let value: string | string[] = decodeURIComponent(keyValue[1]);
        if (value.includes(',')) {
            value = value.split(',');
        }
        paramsObj[key] = value;
    });

    return paramsObj;
};

export const getLastIndexArray = (arr: any[]) => {
    return arr?.length - 1 || 0;
};

export const getDateRange = (startDate: Date, endDate: Date): number => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const range = [];

    while (start <= end) {
        range.push(new Date(start));
        start.setDate(start.getDate() + 1);
    }
    return range.length;
};

export const getQueryParams = <T>(url: string): T => {
    const query = url.split('?')[1];
    const queryObj = {} as T;
    if (query) {
        query.split('&').forEach((item) => {
            const [key, value] = item.split('=');
            queryObj[key] = value;
        });
    }
    return queryObj;
};

export const getQueryParamsValue = (url: string, field: string): string =>
    getQueryParams(url)[field]?.replace(/\+/g, ' ');
